import type { User } from "./types";
import { identityUrl, oauthClientId, callbackUrl } from "./config";
import { safeNextPath } from "@/lib/utils";

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}
export interface Session {
  tokens: TokenPair | null;
  user: User | null;
  status: "unknown" | "guest" | "authenticated";
  generation: number;
  error: string | null;
}
const TOKEN_KEY = "tradestorm.oauth";
const tokenStorage = () => (navigator.locks ? localStorage : sessionStorage);
const TRANSACTION_KEY = "tradestorm.oauth.transaction";
const listeners = new Set<() => void>();
let state: Session = {
  tokens: readTokens(),
  user: null,
  status: "unknown",
  generation: 0,
  error: null,
};
let controller = new AbortController();
let refreshPromise: Promise<string> | null = null;
let restorePromise: Promise<void> | null = null;
let callbackPromise: Promise<string> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

export function readTokens(): TokenPair | null {
  try {
    const t = JSON.parse(tokenStorage().getItem(TOKEN_KEY) || "null");
    return t &&
      typeof t.access_token === "string" &&
      typeof t.refresh_token === "string" &&
      typeof t.expires_at === "number"
      ? t
      : null;
  } catch {
    return null;
  }
}
export const sessionSnapshot = () => state;
export const sessionSignal = () => controller.signal;
export const subscribeSession = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
function publish(next: Partial<Session>) {
  state = { ...state, ...next };
  listeners.forEach((fn) => fn());
}
function scheduleRefresh() {
  clearTimeout(refreshTimer);
  if (!state.tokens) return;
  refreshTimer = setTimeout(
    () => {
      const generation = state.generation;
      void accessToken(true).catch((error: unknown) => {
        // Keep the refresh token on transport failures and retry while offline.
        if (state.tokens && generation === state.generation) {
          publish({ error: error instanceof Error ? error.message : "Session renewal failed" });
          refreshTimer = setTimeout(scheduleRefresh, 10000);
        }
      });
    },
    Math.max(1000, state.tokens.expires_at - Date.now() - 60000)
  );
}
function install(tokens: TokenPair | null, persist = true) {
  if (persist) {
    if (tokens) tokenStorage().setItem(TOKEN_KEY, JSON.stringify(tokens));
    else tokenStorage().removeItem(TOKEN_KEY);
  }
  if (!tokens) {
    for (const key of Object.keys(sessionStorage))
      if (key.startsWith("lab-preview:")) sessionStorage.removeItem(key);
    controller.abort();
    controller = new AbortController();
    publish({
      tokens: null,
      user: null,
      status: "guest",
      generation: state.generation + 1,
      error: null,
    });
  } else publish({ tokens, status: state.user ? "authenticated" : "unknown", error: null });
  scheduleRefresh();
}
async function tokenRequest(body: Record<string, string>): Promise<TokenPair> {
  const response = await fetch(`${identityUrl}/api/auth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    cache: "no-store",
    signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) {
    throw Object.assign(
      new Error(
        response.status >= 500
          ? "Identity service unavailable. Try again."
          : "Your session has expired. Sign in again."
      ),
      { rejected: response.status === 400 || response.status === 401 }
    );
  }
  const pair = await response.json();
  if (
    typeof pair.access_token !== "string" ||
    typeof pair.refresh_token !== "string" ||
    !Number.isFinite(pair.expires_in) ||
    pair.expires_in <= 0
  )
    throw new Error("Invalid identity token response");
  return {
    access_token: pair.access_token,
    refresh_token: pair.refresh_token,
    expires_at: Date.now() + pair.expires_in * 1000,
  };
}
// Unverified subjects only separate UI caches; the trading backend verifies authorization.
function tokenSubject(token?: string): string | undefined {
  try {
    const body = token?.split(".")[1]?.replace(/-/g, "+").replace(/_/g, "/") || "";
    return JSON.parse(atob(body)).sub;
  } catch {
    return undefined;
  }
}
export async function accessToken(force = false): Promise<string> {
  if (!state.tokens) throw new Error("Sign in to continue");
  if (!force && state.tokens.expires_at > Date.now() + 60000) return state.tokens.access_token;
  if (!refreshPromise) {
    const captured = state.tokens;
    const generation = state.generation;
    const refresh = async () => {
      const latest = readTokens();
      if (!latest || generation !== state.generation) throw new Error("Session changed");
      if (tokenSubject(latest.access_token) !== tokenSubject(captured.access_token)) {
        install(null, false);
        install(latest, false);
        setTimeout(() => {
          void restoreSession();
        }, 0);
        throw new Error("Session changed");
      }
      if (latest.access_token !== captured.access_token && latest.expires_at > Date.now() + 60000) {
        install(latest, false);
        return latest.access_token;
      }
      let pair: TokenPair;
      try {
        pair = await tokenRequest({
          grant_type: "refresh_token",
          refresh_token: latest.refresh_token,
        });
      } catch (error) {
        if (
          (error as { rejected?: boolean }).rejected &&
          generation === state.generation &&
          readTokens()?.refresh_token === latest.refresh_token
        )
          install(null);
        throw error;
      }
      if (generation !== state.generation || readTokens()?.refresh_token !== latest.refresh_token) {
        if (readTokens()?.refresh_token !== pair.refresh_token)
          await revokeRefresh(pair.refresh_token);
        throw new Error("Session changed");
      }
      install(pair);
      return pair.access_token;
    };
    // Serialize rotating refresh tokens between tabs, in addition to this tab's single flight.
    refreshPromise = Promise.resolve(
      navigator.locks ? navigator.locks.request("tradestorm.oauth.refresh", refresh) : refresh()
    )
      .then((value) => value)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise!;
}
async function loadIdentity() {
  const generation = state.generation;
  const token = await accessToken();
  if (generation !== state.generation) return;
  const response = await fetch(`${identityUrl}/oauth/userinfo`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
    signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]),
  });
  if (!response.ok) throw new Error("Unable to load your profile");
  const identity = await response.json();
  if (!identity.sub) throw new Error("Invalid identity response");
  if (generation !== state.generation) return;
  const user: User = {
    id: identity.sub,
    name: identity.name || "",
    email: identity.email || "",
    role: "user",
    created_at: "",
    updated_at: "",
  };
  publish({ user, status: "authenticated", error: null });
  scheduleRefresh();
}
/** Reads the name and email again, after the user changed them in their profile. */
export const reloadIdentity = () => loadIdentity();
export function restoreSession(): Promise<void> {
  if (!restorePromise)
    restorePromise = (async () => {
      if (!state.tokens) {
        publish({ status: "guest" });
        return;
      }
      const generation = state.generation;
      try {
        await loadIdentity();
      } catch (error) {
        if (state.tokens && generation === state.generation)
          publish({ error: error instanceof Error ? error.message : "Session restoration failed" });
      }
    })().finally(() => {
      restorePromise = null;
    });
  return restorePromise;
}
const base64url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
export async function createLoginUrl(next = "/") {
  callbackPromise = null;
  if (!oauthClientId) throw new Error("OAuth client is not configured (VITE_OAUTH_CLIENT_ID).");
  if (!globalThis.crypto?.subtle || !globalThis.crypto?.getRandomValues) {
    throw new Error(
      "Sign-in requires browser cryptography. Open the dev server at http://localhost:5173 or use HTTPS. Plain HTTP on a machine IP or hostname does not provide Web Crypto."
    );
  }
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const csrf = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64url(
    new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)))
  );
  sessionStorage.setItem(
    TRANSACTION_KEY,
    JSON.stringify({ verifier, state: csrf, next: safeNextPath(next), created: Date.now() })
  );
  const params = new URLSearchParams({
    response_type: "code",
    client_id: oauthClientId,
    redirect_uri: callbackUrl(),
    scope: "openid profile email",
    state: csrf,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  return `${identityUrl}/oauth/authorize?${params}`;
}
export async function beginLogin(next = "/") {
  location.assign(await createLoginUrl(next));
}
export function completeLogin(search: string): Promise<string> {
  // React StrictMode can mount the callback twice; codes are single-use.
  if (!callbackPromise)
    callbackPromise = (async () => {
      const params = new URLSearchParams(search);
      const transaction = JSON.parse(sessionStorage.getItem(TRANSACTION_KEY) || "null");
      sessionStorage.removeItem(TRANSACTION_KEY);
      history.replaceState(null, "", "/oauth/callback");
      if (
        !transaction ||
        typeof transaction.verifier !== "string" ||
        typeof transaction.created !== "number" ||
        params.get("state") !== transaction.state ||
        Date.now() - transaction.created > 600000
      )
        throw new Error("Login expired or state did not match. Sign in again.");
      if (params.has("error"))
        throw new Error(params.get("error_description") || params.get("error") || "Sign in failed");
      const code = params.get("code");
      if (!code) throw new Error("Missing authorization code");
      const generation = state.generation;
      const pair = await tokenRequest({
        grant_type: "authorization_code",
        client_id: oauthClientId,
        redirect_uri: callbackUrl(),
        code,
        code_verifier: transaction.verifier,
      });
      if (generation !== state.generation) {
        await revokeRefresh(pair.refresh_token);
        throw new Error("Session changed");
      }
      // A successful new login invalidates all work from the previous identity.
      install(null);
      install(pair);
      await loadIdentity();
      return safeNextPath(transaction.next);
    })();
  return callbackPromise;
}
async function revokeRefresh(refresh: string) {
  await fetch(`${identityUrl}/api/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refresh }),
    signal: AbortSignal.timeout(5000),
  }).catch(() => {});
}
/** The SSO's browser sign-out: clears its session cookie, then returns to this app's login page. */
export function signOutUrl() {
  const next = new URL("/login", location.origin).href;
  return `${identityUrl}/oauth/logout?${new URLSearchParams({ next })}`;
}
let signingOut = false;
/** True from signOut() until the page leaves for the SSO: no new sign-in may start meanwhile. */
export const isSigningOut = () => signingOut;
/**
 * Ends this app's session and the SSO's, so the next sign-in asks for an
 * account again. Clearing the session sends the router to /login, whose
 * automatic redirect to the SSO would otherwise race this navigation and
 * sign the same account straight back in.
 */
export async function signOut() {
  signingOut = true;
  try {
    await logout();
  } finally {
    location.assign(signOutUrl());
  }
}
export async function logout() {
  const refresh = state.tokens?.refresh_token;
  install(null);
  sessionStorage.removeItem(TRANSACTION_KEY);
  callbackPromise = null;
  if (refresh) await revokeRefresh(refresh);
}
window.addEventListener("storage", (event) => {
  if (!navigator.locks || event.key !== TOKEN_KEY) return;
  const next = readTokens();
  const old = state.tokens;
  if (next?.access_token === old?.access_token) return;
  // Token refresh preserves the identity; a different subject clears all cached data.
  if (!next || tokenSubject(next.access_token) !== tokenSubject(old?.access_token))
    install(null, false);
  if (next) {
    install(next, false);
    void restoreSession();
  }
});
window.addEventListener("online", () => {
  void restoreSession();
});

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void restoreSession();
});
